import { useDispatch, useSelector } from "react-redux";
import { BusinessParams, ConfigStepId, ConfigStepType, SignatureStatus } from "../../utils/configuration";
import { AppDispatch, RootState } from "../../configureStore";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useIsMobile from "../../utils/useIsMobile";
import {
    ApplicantBusiness,
    checkBusinessSignatureCompleted,
    getService,
    SigSignSignature,
    addBusiness,
    createBusinessSignature,
} from "../../redux/reducer/applicant";
import {
    Skeleton,
    Card,
    Badge,
    Typography,
    Space,
    Button,
    Row,
    Col,
    Empty,
    Divider,
} from "antd";
import { AxiosResponse } from "axios";
import { SUCCESSFUL_MESSAGE } from "../../redux/reducer/status";
import ErrorPage from "./ErrorPage";
import {
    PlusOutlined,
    CheckCircleOutlined,
    LoadingOutlined,
    BankOutlined,
    MailOutlined,
    IdcardOutlined,
    HomeOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

interface ActiveSignature extends SigSignSignature {
    businessId?: string;
}

const StatusBadge = ({ business }: { business: ApplicantBusiness }) => {
    if (business.completed) {
        return <Badge status="success" text="Complete" />;
    }
    if (business.signature.some(s => s.status === 'Declined')) {
        return <Badge status="error" text="Declined" />;
    }
    if (business.signature.some(s => !s.completed)) {
        return <Badge status="processing" text="Pending Signature" />;
    }
    return <Badge status="default" text="Not Started" />;
};

interface BusinessCardProps {
    business: ApplicantBusiness;
    isActive: boolean;
}

const BusinessCard = ({ business, isActive }: BusinessCardProps) => {
    const formatAddress = (address: ApplicantBusiness['address']) => {
        const parts = [
            address.lineOne,
            address.lineTwo,
            [address.city, address.state, address.postalCode].filter(Boolean).join(', ')
        ].filter(Boolean);
        return parts.join(', ');
    };

    return (
        <Card
            hoverable={!business.completed}
            style={{
                marginBottom: 16,
                borderColor: isActive ? '#1890ff' : undefined,
                borderWidth: isActive ? 2 : 1,
            }}
        >
            <Row justify="space-between" align="middle">
                <Col flex="auto">
                    <Space direction="vertical" size="small">
                        <Space>
                            <BankOutlined style={{ fontSize: 18, color: '#1890ff' }} />
                            <Title level={5} style={{ margin: 0 }}>{business.name || 'Unnamed Business'}</Title>
                        </Space>
                        {business.ein && (
                            <Text type="secondary">
                                <IdcardOutlined style={{ marginRight: 8 }} />
                                EIN: {business.ein}
                            </Text>
                        )}
                        {business.title && (
                            <Text type="secondary">Title: {business.title}</Text>
                        )}
                        {business.email && (
                            <Text type="secondary">
                                <MailOutlined style={{ marginRight: 8 }} />
                                {business.email}
                            </Text>
                        )}
                        {business.address && formatAddress(business.address) && (
                            <Text type="secondary">
                                <HomeOutlined style={{ marginRight: 8 }} />
                                {formatAddress(business.address)}
                            </Text>
                        )}
                    </Space>
                </Col>
                <Col>
                    <StatusBadge business={business} />
                </Col>
            </Row>
        </Card>
    );
};


const Business = ({
    allowAddition,
    signatureMode,
}: BusinessParams) => {
    const isMobile = useIsMobile();
    const [iframeHeight, setIframeHeight] = useState<number | null>(null);

    const steps = useSelector<RootState, ConfigStepType[]>(({ configuration: { steps } }) => steps);
    const businesses = useSelector<RootState, ApplicantBusiness[]>(
        ({ applicant }) => applicant.business?.businesses ?? []
    );
    const sharedSignature = useSelector<RootState, SigSignSignature | undefined>(
        ({ applicant }) => applicant.business?.sharedSignature
    );
    const loading = useSelector<RootState, boolean>(
        ({ status: { loading: l } }) => l['applicant/GET_SERVICE_INFO']
    );
    const addingBusiness = useSelector<RootState, boolean>(
        ({ status: { loading: l } }) => l['applicant/ADD_BUSINESS']
    );
    const creatingSignature = useSelector<RootState, boolean>(
        ({ status: { loading: l } }) => l['applicant/CREATE_BUSINESS_SIGNATURE']
    );

    const hasError = useMemo(() =>
        businesses.some(b => b.signature.some(s => s.status === 'Declined')) ||
        sharedSignature?.status === 'Declined',
        [businesses, sharedSignature]
    );

    const allComplete = useMemo(() => {
        if (businesses.length === 0) return false;
        if (signatureMode === 'shared') {
            return sharedSignature?.completed ?? false;
        }
        return businesses.every(b => b.completed);
    }, [businesses, signatureMode, sharedSignature]);

    const activeSignature = useMemo<ActiveSignature | undefined>(() => {
        if (signatureMode === 'shared') {
            if (sharedSignature && !sharedSignature.completed) {
                return sharedSignature;
            }
            return undefined;
        }

        for (const business of businesses) {
            const incomplete = business.signature.find(s => !s.completed);
            if (incomplete) {
                return { ...incomplete, businessId: business.id };
            }
        }
        return undefined;
    }, [signatureMode, sharedSignature, businesses]);

    const dispatch = useDispatch<AppDispatch>();

    const fetchBusinessData = useCallback(() => {
        const service = steps.find(({ id }) => id === ConfigStepId.business);
        if (service) {
            dispatch(getService({
                input: { type: ConfigStepId.business },
                headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
            }));
        }
    }, [steps, dispatch]);

    const hasCalled = useRef(false);

    useEffect(() => {
        if (hasCalled.current) return;
        hasCalled.current = true;
        fetchBusinessData();
    }, [fetchBusinessData]);

    useEffect(() => {
        const interval = setInterval(async () => {
            try {
                if (activeSignature && !hasError) {
                    dispatch(checkBusinessSignatureCompleted({
                        input: {
                            transactionId: activeSignature.transactionId,
                            businessId: activeSignature.businessId,
                        },
                        onSuccess: (res: AxiosResponse<{ completed: boolean; transactionId?: string; status: SignatureStatus }>) => {
                            if (res.data.completed) {
                                dispatch({ type: SUCCESSFUL_MESSAGE, message: 'We have received your completed document!' });
                            }
                        },
                    }));
                }
            } catch (err) {
                console.error(err);
            }
        }, 3000);

        return () => clearInterval(interval);
    }, [activeSignature, hasError, dispatch]);

    useEffect(() => {
        const handleEvent = (event: MessageEvent) => {
            if (event.origin) {
                switch (event.data.event) {
                    case 'resize':
                    case 'iframeHeight':
                    case 'contentHeight':
                        if (event.data.height && typeof event.data.height === 'number') {
                            setIframeHeight(event.data.height);
                        }
                        break;
                    default:
                        console.warn('Unrecognized event', event.data);
                }
            }
        };

        window.addEventListener("message", handleEvent);
        return () => window.removeEventListener("message", handleEvent);
    }, []);

    useEffect(() => {
        if (creatingSignature) return;

        if (signatureMode === 'shared') {
            if (!sharedSignature) {
                dispatch(createBusinessSignature({
                    input: {},
                    headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
                }));
            }
        } else {
            const businessIndex = businesses.findIndex(b =>
                !b.completed && b.signature.length === 0
            );

            if (businessIndex !== -1) {
                dispatch(createBusinessSignature({
                    input: { businessIndex },
                    headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
                }));
            }
        }
    }, [businesses, creatingSignature, dispatch, signatureMode, sharedSignature]);

    const handleAddBusiness = useCallback(() => {
        dispatch(addBusiness({
            input: {},
            headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
            onSuccess: () => {
                fetchBusinessData();
            },
        }));
    }, [dispatch, fetchBusinessData]);

    if (hasError) {
        return (
            <ErrorPage
                title="Signature was declined"
                subTitle="We cannot proceed without your electronic signature consent for business authorization"
            />
        );
    }

    if (allComplete) {
        return (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a', marginBottom: 24 }} />
                <Title level={3}>All Business Authorizations Complete</Title>
                <Text type="secondary">
                    Thank you for completing the business authorization process.
                </Text>
            </div>
        );
    }

    if (loading && businesses.length === 0) {
        return <Skeleton active paragraph={{ rows: 4 }} />;
    }

    return (
        <div style={{ padding: '0 16px' }}>
            {businesses.length === 0 ? (
                <Empty
                    description={
                        <Space direction="vertical">
                            <Text>No businesses added yet</Text>
                            {allowAddition && (
                                <Text type="secondary">
                                    Click below to add your first business
                                </Text>
                            )}
                        </Space>
                    }
                >
                    {allowAddition && (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleAddBusiness}
                            loading={addingBusiness}
                        >
                            Add Business
                        </Button>
                    )}
                </Empty>
            ) : (
                <>
                    {businesses.map((business) => (
                        <BusinessCard
                            key={business.id}
                            business={business}
                            isActive={activeSignature?.businessId === business.id}
                        />
                    ))}

                    {allowAddition && (
                        <Button
                            type="dashed"
                            icon={<PlusOutlined />}
                            onClick={handleAddBusiness}
                            loading={addingBusiness}
                            style={{ width: '100%', marginBottom: 16 }}
                        >
                            Add Another Business
                        </Button>
                    )}

                    {activeSignature && (
                        <>
                            <Divider>Sign Authorization Document</Divider>
                            <Skeleton loading={!activeSignature.embedUrl} active>
                                <iframe
                                    title="Business Authorization Signature"
                                    allow="camera *"
                                    src={activeSignature.embedUrl}
                                    className="signature_frame"
                                    style={{
                                        height: iframeHeight ? `${iframeHeight}px` : (isMobile ? '100vh' : '80vh'),
                                        minHeight: '1200px',
                                        width: '100%',
                                        border: 'none',
                                        overflow: 'hidden'
                                    }}
                                />
                            </Skeleton>
                        </>
                    )}

                    {!activeSignature && businesses.length > 0 && !allComplete && (
                        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                            <LoadingOutlined style={{ fontSize: 32, marginBottom: 16 }} />
                            <Text>Loading signature request...</Text>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Business;
